import { supabase } from '../lib/supabase';

export const n8nWebhookLogsService = {
  /**
   * Fetch all webhook logs with optional filters
   * @param {Object} filters - { status, containerReference, dateFrom, dateTo }
   * @returns {Promise<Array>} Array of webhook logs
   */
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('n8n_webhook_logs')?.select(`
          *,
          containers (
            container_code,
            status
          )
        `)?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.containerReference) {
        query = query?.eq('containers.container_code', filters?.containerReference);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert snake_case to camelCase
      return data?.map(log => ({
        id: log?.id,
        containerId: log?.container_id,
        webhookUrl: log?.webhook_url,
        payload: log?.payload,
        status: log?.status,
        httpStatusCode: log?.http_status_code,
        responseBody: log?.response_body,
        errorMessage: log?.error_message,
        retryCount: log?.retry_count,
        lastRetryAt: log?.last_retry_at,
        createdAt: log?.created_at,
        updatedAt: log?.updated_at,
        container: log?.containers ? {
          containerCode: log?.containers?.container_code,
          status: log?.containers?.status
        } : null
      }));
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
      throw error;
    }
  },

  /**
   * Get webhook log by ID
   * @param {string} id - Webhook log ID
   * @returns {Promise<Object>} Webhook log details
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('n8n_webhook_logs')?.select(`
          *,
          containers (
            container_code,
            status
          )
        `)?.eq('id', id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        containerId: data?.container_id,
        webhookUrl: data?.webhook_url,
        payload: data?.payload,
        status: data?.status,
        httpStatusCode: data?.http_status_code,
        responseBody: data?.response_body,
        errorMessage: data?.error_message,
        retryCount: data?.retry_count,
        lastRetryAt: data?.last_retry_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        container: data?.containers ? {
          containerCode: data?.containers?.container_code,
          status: data?.containers?.status
        } : null
      };
    } catch (error) {
      console.error('Error fetching webhook log:', error);
      throw error;
    }
  },

  /**
   * Retry policy configuration
   */
  retryPolicyConfig: {
    maxRetries: 5, // Maximum number of retry attempts
    baseDelay: 1000, // Base delay in ms (1 second)
    maxDelay: 60000, // Maximum delay in ms (60 seconds)
    backoffMultiplier: 2, // Exponential backoff multiplier
    failureThreshold: 10, // Number of consecutive failures before moving to DLQ
    retryableStatusCodes: [408, 429, 500, 502, 503, 504], // HTTP codes to retry
    enableDeadLetterQueue: true
  },

  /**
   * Get current retry policy configuration
   * @returns {Promise<Object>} Current retry policy config
   */
  async getRetryPolicyConfig() {
    try {
      // Fetch from database if stored there, otherwise return default
      const { data, error } = await supabase
        ?.from('webhook_retry_config')
        ?.select('*')
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error; // Ignore not found error

      return data || this.retryPolicyConfig;
    } catch (err) {
      console.log('Using default retry policy config:', err);
      return this.retryPolicyConfig;
    }
  },

  /**
   * Update retry policy configuration
   * @param {Object} config - New retry policy configuration
   * @returns {Promise<Object>} Updated config
   */
  async updateRetryPolicyConfig(config) {
    try {
      const { data, error } = await supabase
        ?.from('webhook_retry_config')
        ?.upsert({
          id: 1, // Single config row
          max_retries: config?.maxRetries,
          base_delay: config?.baseDelay,
          max_delay: config?.maxDelay,
          backoff_multiplier: config?.backoffMultiplier,
          failure_threshold: config?.failureThreshold,
          retryable_status_codes: config?.retryableStatusCodes,
          enable_dead_letter_queue: config?.enableDeadLetterQueue,
          updated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Update in-memory config
      this.retryPolicyConfig = config;

      return {
        maxRetries: data?.max_retries,
        baseDelay: data?.base_delay,
        maxDelay: data?.max_delay,
        backoffMultiplier: data?.backoff_multiplier,
        failureThreshold: data?.failure_threshold,
        retryableStatusCodes: data?.retryable_status_codes,
        enableDeadLetterQueue: data?.enable_dead_letter_queue
      };
    } catch (err) {
      console.error('Error updating retry policy config:', err);
      throw err;
    }
  },

  /**
   * Calculate exponential backoff delay
   * @param {number} retryCount - Current retry attempt number
   * @param {Object} config - Retry policy configuration
   * @returns {number} Delay in milliseconds
   */
  calculateBackoffDelay(retryCount, config) {
    const delay = config?.baseDelay * Math?.pow(config?.backoffMultiplier, retryCount);
    return Math?.min(delay, config?.maxDelay);
  },

  /**
   * Check if HTTP status code is retryable
   * @param {number} statusCode - HTTP status code
   * @param {Object} config - Retry policy configuration
   * @returns {boolean} True if retryable
   */
  isRetryableStatus(statusCode, config) {
    // Ensure statusCode is a number for proper comparison
    const normalizedStatusCode = typeof statusCode === 'string' ? parseInt(statusCode, 10) : statusCode;
    
    // Ensure retryableStatusCodes are numbers
    const normalizedRetryableCodes = config?.retryableStatusCodes?.map(code => 
      typeof code === 'string' ? parseInt(code, 10) : code
    ) || [];
    
    return normalizedRetryableCodes?.includes(normalizedStatusCode);
  },

  /**
   * Retry a failed webhook delivery with exponential backoff
   * @param {string} id - Webhook log ID
   * @param {Object} customConfig - Optional custom retry config
   * @returns {Promise<Object>} Updated webhook log
   */
  async retry(id, customConfig = null) {
    try {
      // Get webhook log details
      const log = await this.getById(id);
      
      // Get retry policy config
      const config = customConfig || (await this.getRetryPolicyConfig());

      // Log the actual config being used for debugging
      console.log('🔧 Retry config:', {
        maxRetries: config?.maxRetries,
        retryableStatusCodes: config?.retryableStatusCodes,
        currentHttpStatus: log?.httpStatusCode,
        currentRetryCount: log?.retryCount
      });

      // Check if max retries exceeded
      if (log?.retryCount >= config?.maxRetries) {
        if (config?.enableDeadLetterQueue) {
          await this.moveToDeadLetterQueue(id, 'Max retries exceeded');
        }
        throw new Error(`Max retries (${config?.maxRetries}) exceeded for webhook ${id}`);
      }

      // Check if status code is retryable - but only if this isn't the first attempt
      // On first retry attempt after initial failure, we should always try regardless
      if (log?.httpStatusCode && log?.retryCount > 0 && !this.isRetryableStatus(log?.httpStatusCode, config)) {
        console.warn(`⚠️ Status code ${log?.httpStatusCode} not in retryable list: ${JSON.stringify(config?.retryableStatusCodes)}`);
        if (config?.enableDeadLetterQueue) {
          await this.moveToDeadLetterQueue(id, `Non-retryable status code after ${log?.retryCount} attempts: ${log?.httpStatusCode}`);
        }
        throw new Error(`Status code ${log?.httpStatusCode} is not retryable after ${log?.retryCount} attempts. Retryable codes: ${config?.retryableStatusCodes?.join(', ')}`);
      }

      // Calculate backoff delay
      const backoffDelay = this.calculateBackoffDelay(log?.retryCount, config);
      console.log(`⏳ Waiting ${backoffDelay}ms before retry attempt ${log?.retryCount + 1}...`);

      // Wait for backoff delay
      await new Promise(resolve => setTimeout(resolve, backoffDelay));

      // Update status to retrying
      await supabase?.from('n8n_webhook_logs')?.update({
          status: 'retrying',
          updated_at: new Date()?.toISOString()
        })?.eq('id', id);

      // Send webhook request
      const response = await fetch(log?.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Retry-Count': log?.retryCount?.toString(),
          'X-Retry-Delay': backoffDelay?.toString()
        },
        body: JSON.stringify(log?.payload)
      });

      const responseBody = await response?.text();
      const newRetryCount = log?.retryCount + 1;

      // Determine if this is a transient failure that should be retried
      const isTransientFailure = !response?.ok && 
        (this.isRetryableStatus(response?.status, config) || 
         response?.status === 0); // Network errors

      // Update log with retry result
      const updateData = {
        status: response?.ok ? 'success' : (isTransientFailure && newRetryCount < config?.maxRetries ? 'failed' : 'dead_letter'),
        http_status_code: response?.status,
        response_body: responseBody,
        error_message: response?.ok ? null : `HTTP ${response?.status}: ${responseBody}`,
        retry_count: newRetryCount,
        last_retry_at: new Date()?.toISOString(),
        next_retry_at: !response?.ok && isTransientFailure && newRetryCount < config?.maxRetries
          ? new Date(Date?.now() + this.calculateBackoffDelay(newRetryCount, config))?.toISOString()
          : null,
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('n8n_webhook_logs')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      // If permanently failed and DLQ enabled, move to DLQ
      if (updateData?.status === 'dead_letter' && config?.enableDeadLetterQueue) {
        await this.moveToDeadLetterQueue(id, `Failed after ${newRetryCount} retry attempts`);
      }

      return {
        id: data?.id,
        status: data?.status,
        httpStatusCode: data?.http_status_code,
        responseBody: data?.response_body,
        errorMessage: data?.error_message,
        retryCount: data?.retry_count,
        lastRetryAt: data?.last_retry_at,
        nextRetryAt: data?.next_retry_at
      };
    } catch (error) {
      console.error('Error retrying webhook:', error);
      
      // Update log with error
      await supabase?.from('n8n_webhook_logs')?.update({
          status: 'failed',
          error_message: error?.message,
          retry_count: (await this.getById(id))?.retryCount + 1,
          last_retry_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })?.eq('id', id);
      
      throw error;
    }
  },

  /**
   * Move webhook log to dead-letter queue
   * @param {string} logId - Webhook log ID
   * @param {string} reason - Reason for moving to DLQ
   * @returns {Promise<Object>} DLQ entry
   */
  async moveToDeadLetterQueue(logId, reason) {
    try {
      // Get full log details
      const log = await this.getById(logId);

      // Insert into dead-letter queue
      const { data, error } = await supabase?.from('webhook_dead_letter_queue')?.insert({
          original_log_id: logId,
          container_id: log?.containerId,
          webhook_url: log?.webhookUrl,
          payload: log?.payload,
          http_status_code: log?.httpStatusCode,
          error_message: log?.errorMessage,
          retry_count: log?.retryCount,
          reason: reason,
          moved_at: new Date()?.toISOString()
        })?.select()?.single();

      if (error) throw error;

      console.log(`📥 Moved webhook ${logId} to dead-letter queue:`, reason);

      return {
        id: data?.id,
        originalLogId: data?.original_log_id,
        reason: data?.reason,
        movedAt: data?.moved_at
      };
    } catch (err) {
      console.error('Error moving to dead-letter queue:', err);
      throw err;
    }
  },

  /**
   * Get dead-letter queue entries
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} DLQ entries
   */
  async getDeadLetterQueue(filters = {}) {
    try {
      let query = supabase?.from('webhook_dead_letter_queue')?.select(`
          *,
          containers (
            container_code,
            status
          )
        `)?.order('moved_at', { ascending: false });

      if (filters?.containerId) {
        query = query?.eq('container_id', filters?.containerId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('moved_at', filters?.dateFrom);
      }

      if (filters?.dateTo) {
        query = query?.lte('moved_at', filters?.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(entry => ({
        id: entry?.id,
        originalLogId: entry?.original_log_id,
        containerId: entry?.container_id,
        webhookUrl: entry?.webhook_url,
        payload: entry?.payload,
        httpStatusCode: entry?.http_status_code,
        errorMessage: entry?.error_message,
        retryCount: entry?.retry_count,
        reason: entry?.reason,
        movedAt: entry?.moved_at,
        container: entry?.containers ? {
          containerCode: entry?.containers?.container_code,
          status: entry?.containers?.status
        } : null
      }));
    } catch (err) {
      console.error('Error fetching dead-letter queue:', err);
      throw err;
    }
  },

  /**
   * Reprocess item from dead-letter queue
   * @param {string} dlqId - Dead-letter queue entry ID
   * @returns {Promise<Object>} Result of reprocessing
   */
  async reprocessFromDeadLetterQueue(dlqId) {
    try {
      // Get DLQ entry
      const { data: dlqEntry, error: fetchError } = await supabase
        ?.from('webhook_dead_letter_queue')
        ?.select('*')
        ?.eq('id', dlqId)
        ?.single();

      if (fetchError) throw fetchError;

      // Create new webhook log entry
      const { data: newLog, error: insertError } = await supabase
        ?.from('n8n_webhook_logs')
        ?.insert({
          container_id: dlqEntry?.container_id,
          webhook_url: dlqEntry?.webhook_url,
          payload: dlqEntry?.payload,
          status: 'pending',
          retry_count: 0,
          created_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (insertError) throw insertError;

      // Try to deliver webhook
      const retryResult = await this.retry(newLog?.id);

      // If successful, delete from DLQ
      if (retryResult?.status === 'success') {
        await supabase?.from('webhook_dead_letter_queue')?.delete()?.eq('id', dlqId);
        console.log(`✅ Successfully reprocessed and removed from DLQ: ${dlqId}`);
      }

      return {
        dlqId,
        newLogId: newLog?.id,
        status: retryResult?.status,
        message: retryResult?.status === 'success' ?'Successfully reprocessed and removed from DLQ' :'Reprocessing failed, entry remains in DLQ'
      };
    } catch (err) {
      console.error('Error reprocessing from DLQ:', err);
      throw err;
    }
  },

  /**
   * Auto-retry failed webhooks with configurable policy
   * @returns {Promise<Object>} Results of auto-retry
   */
  async autoRetryFailedWebhooks() {
    try {
      const config = await this.getRetryPolicyConfig();
      
      // Get all failed webhooks eligible for retry
      const { data: failedLogs, error } = await supabase
        ?.from('n8n_webhook_logs')
        ?.select('*')
        ?.eq('status', 'failed')
        ?.lt('retry_count', config?.maxRetries)
        ?.or(`next_retry_at.is.null,next_retry_at.lte.${new Date()?.toISOString()}`);

      if (error) throw error;

      const results = {
        total: failedLogs?.length,
        success: 0,
        failed: 0,
        movedToDLQ: 0,
        details: []
      };

      // Process each failed log
      for (const log of failedLogs) {
        try {
          const result = await this.retry(log?.id);
          
          if (result?.status === 'success') {
            results.success++;
          } else if (result?.status === 'dead_letter') {
            results.movedToDLQ++;
          } else {
            results.failed++;
          }

          results?.details?.push({
            id: log?.id,
            status: result?.status,
            retryCount: result?.retryCount
          });
        } catch (err) {
          results.failed++;
          results?.details?.push({
            id: log?.id,
            status: 'error',
            error: err?.message
          });
        }
      }

      console.log('🔄 Auto-retry completed:', results);
      return results;
    } catch (err) {
      console.error('Error in auto-retry:', err);
      throw err;
    }
  },

  /**
   * Get webhook statistics
   * @returns {Promise<Object>} Statistics object
   */
  async getStatistics() {
    try {
      const { data, error } = await supabase?.from('n8n_webhook_logs')?.select('status, retry_count');

      if (error) throw error;

      const stats = {
        total: data?.length,
        success: data?.filter(log => log?.status === 'success')?.length,
        failed: data?.filter(log => log?.status === 'failed')?.length,
        pending: data?.filter(log => log?.status === 'pending')?.length,
        retrying: data?.filter(log => log?.status === 'retrying')?.length,
        totalRetries: data?.reduce((sum, log) => sum + (log?.retry_count || 0), 0),
        successRate: 0
      };

      if (stats?.total > 0) {
        stats.successRate = ((stats?.success / stats?.total) * 100)?.toFixed(1);
      }

      return stats;
    } catch (error) {
      console.error('Error fetching webhook statistics:', error);
      throw error;
    }
  },

  /**
   * Get recent webhook activity
   * @param {number} limit - Number of recent logs to fetch
   * @returns {Promise<Array>} Recent webhook logs
   */
  async getRecent(limit = 10) {
    try {
      const { data, error } = await supabase?.from('n8n_webhook_logs')?.select(`
          *,
          containers (
            container_code
          )
        `)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        status: log?.status,
        httpStatusCode: log?.http_status_code,
        retryCount: log?.retry_count,
        createdAt: log?.created_at,
        containerReference: log?.containers?.container_code || 'N/A'
      }));
    } catch (error) {
      console.error('Error fetching recent webhooks:', error);
      throw error;
    }
  },

  /**
   * Batch retry multiple failed webhooks
   * @param {Array<string>} logIds - Array of webhook log IDs to retry
   * @returns {Promise<Object>} Results with success and failure counts
   */
  async batchRetry(logIds) {
    try {
      const results = {
        total: logIds?.length,
        success: 0,
        failed: 0,
        details: []
      };

      // Process retries sequentially to avoid overwhelming the webhook endpoint
      for (const logId of logIds) {
        try {
          const result = await this.retry(logId);
          
          if (result?.status === 'success') {
            results.success++;
          } else {
            results.failed++;
          }

          results?.details?.push({
            id: logId,
            status: result?.status,
            message: result?.status === 'success' ? 'Retry successful' : result?.errorMessage
          });
        } catch (error) {
          results.failed++;
          results?.details?.push({
            id: logId,
            status: 'failed',
            message: error?.message
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error in batch retry:', error);
      throw error;
    }
  },

  /**
   * Get retry success rate analytics
   * @returns {Promise<Object>} Retry success rate statistics
   */
  async getRetrySuccessAnalytics() {
    try {
      const { data, error } = await supabase?.from('n8n_webhook_logs')?.select('status, retry_count, created_at')?.gt('retry_count', 0);

      if (error) throw error;

      const totalRetries = data?.length;
      const successfulRetries = data?.filter(log => log?.status === 'success')?.length;
      const failedRetries = data?.filter(log => log?.status === 'failed')?.length;

      // Group by retry count
      const retryDistribution = {};
      data?.forEach(log => {
        const count = log?.retry_count;
        if (!retryDistribution?.[count]) {
          retryDistribution[count] = { total: 0, success: 0, failed: 0 };
        }
        retryDistribution[count].total++;
        if (log?.status === 'success') retryDistribution[count].success++;
        if (log?.status === 'failed') retryDistribution[count].failed++;
      });

      return {
        totalRetries,
        successfulRetries,
        failedRetries,
        successRate: totalRetries > 0 ? ((successfulRetries / totalRetries) * 100)?.toFixed(1) : 0,
        retryDistribution: Object?.keys(retryDistribution)?.map(key => ({
          retryCount: parseInt(key),
          total: retryDistribution?.[key]?.total,
          success: retryDistribution?.[key]?.success,
          failed: retryDistribution?.[key]?.failed,
          successRate: ((retryDistribution?.[key]?.success / retryDistribution?.[key]?.total) * 100)?.toFixed(1)
        }))?.sort((a, b) => a?.retryCount - b?.retryCount)
      };
    } catch (error) {
      console.error('Error fetching retry success analytics:', error);
      throw error;
    }
  },

  /**
   * Get failure pattern trends over time
   * @param {number} days - Number of days to analyze (default: 30)
   * @returns {Promise<Array>} Daily failure trends
   */
  async getFailureTrends(days = 30) {
    try {
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      const { data, error } = await supabase?.from('n8n_webhook_logs')?.select('status, error_message, created_at')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by date
      const dailyStats = {};
      data?.forEach(log => {
        const date = new Date(log?.created_at)?.toLocaleDateString();
        if (!dailyStats?.[date]) {
          dailyStats[date] = {
            date,
            total: 0,
            success: 0,
            failed: 0,
            pending: 0,
            retrying: 0
          };
        }
        dailyStats[date].total++;
        dailyStats[date][log?.status]++;
      });

      // Calculate error pattern frequencies
      const errorPatterns = {};
      data?.filter(log => log?.error_message)?.forEach(log => {
        const errorType = log?.error_message?.substring(0, 50); // First 50 chars as pattern
        errorPatterns[errorType] = (errorPatterns?.[errorType] || 0) + 1;
      });

      return {
        dailyTrends: Object?.values(dailyStats)?.map(stat => ({
          ...stat,
          failureRate: stat?.total > 0 ? ((stat?.failed / stat?.total) * 100)?.toFixed(1) : 0
        })),
        commonErrorPatterns: Object?.entries(errorPatterns)?.map(([pattern, count]) => ({ pattern, count }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 10)
      };
    } catch (error) {
      console.error('Error fetching failure trends:', error);
      throw error;
    }
  },

  /**
   * Get peak failure times analysis
   * @returns {Promise<Object>} Hourly failure distribution
   */
  async getPeakFailureTimes() {
    try {
      const { data, error } = await supabase?.from('n8n_webhook_logs')?.select('status, created_at')?.eq('status', 'failed');

      if (error) throw error;

      // Group by hour
      const hourlyStats = Array(24)?.fill(0)?.map((_, hour) => ({
        hour,
        failures: 0,
        hourLabel: `${hour?.toString()?.padStart(2, '0')}:00`
      }));

      data?.forEach(log => {
        const hour = new Date(log?.created_at)?.getHours();
        hourlyStats[hour].failures++;
      });

      // Find peak hours
      const maxFailures = Math?.max(...hourlyStats?.map(h => h?.failures));
      const peakHours = hourlyStats?.filter(h => h?.failures === maxFailures)?.map(h => h?.hourLabel);

      return {
        hourlyDistribution: hourlyStats,
        peakHours,
        maxFailures,
        totalFailures: data?.length
      };
    } catch (error) {
      console.error('Error fetching peak failure times:', error);
      throw error;
    }
  },

  /**
   * Get container-specific failure analysis
   * @returns {Promise<Array>} Container failure statistics
   */
  async getContainerFailureAnalysis() {
    try {
      const { data, error } = await supabase?.from('n8n_webhook_logs')?.select(`
          *,
          containers (
            container_code,
            status
          )
        `);

      if (error) throw error;

      // Group by container
      const containerStats = {};
      data?.forEach(log => {
        const containerId = log?.container_id;
        if (!containerId) return;

        if (!containerStats?.[containerId]) {
          containerStats[containerId] = {
            containerId,
            containerReference: log?.containers?.container_code || 'Unknown',
            containerCode: log?.containers?.container_code || 'N/A',
            containerStatus: log?.containers?.status || 'N/A',
            total: 0,
            success: 0,
            failed: 0,
            pending: 0,
            retrying: 0,
            totalRetries: 0
          };
        }

        containerStats[containerId].total++;
        containerStats[containerId][log?.status]++;
        containerStats[containerId].totalRetries += log?.retry_count || 0;
      });

      return Object?.values(containerStats)?.map(stats => ({
        ...stats,
        failureRate: stats?.total > 0 ? ((stats?.failed / stats?.total) * 100)?.toFixed(1) : 0,
        successRate: stats?.total > 0 ? ((stats?.success / stats?.total) * 100)?.toFixed(1) : 0,
        avgRetries: stats?.total > 0 ? (stats?.totalRetries / stats?.total)?.toFixed(2) : 0
      }))?.sort((a, b) => b?.failureRate - a?.failureRate);
    } catch (error) {
      console.error('Error fetching container failure analysis:', error);
      throw error;
    }
  },

  /**
   * Get comprehensive analytics dashboard data
   * @returns {Promise<Object>} All analytics data
   */
  async getAnalyticsDashboard() {
    try {
      const [
        retryAnalytics,
        failureTrends,
        peakTimes,
        containerAnalysis
      ] = await Promise.all([
        this.getRetrySuccessAnalytics(),
        this.getFailureTrends(30),
        this.getPeakFailureTimes(),
        this.getContainerFailureAnalysis()
      ]);

      return {
        retryAnalytics,
        failureTrends,
        peakTimes,
        containerAnalysis: containerAnalysis?.slice(0, 10) // Top 10 containers
      };
    } catch (error) {
      console.error('Error fetching analytics dashboard:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time webhook log updates
   * @param {Function} onInsert - Callback when new log is inserted
   * @param {Function} onUpdate - Callback when log is updated
   * @param {Function} onDelete - Callback when log is deleted (optional)
   * @param {Function} onConnectionChange - Callback when connection status changes (optional)
   * @returns {Object} Subscription channel
   */
  subscribeToWebhookUpdates(onInsert, onUpdate, onDelete = null, onConnectionChange = null) {
    try {
      console.log('🔌 Setting up n8n_webhook_logs realtime subscription...');
      
      // Create unique channel name to avoid conflicts
      const channelName = `n8n_webhook_logs_realtime_${Date.now()}`;
      
      const channel = supabase
        ?.channel(channelName)
        ?.on(
          'postgres_changes',
          { 
            event: 'INSERT', 
            schema: 'public', 
            table: 'n8n_webhook_logs'
            // Remove filter to avoid schema binding issues with complex JSONB types
          },
          (payload) => {
            console.log('🔔 New webhook log received:', payload);
            if (onInsert && payload?.new) {
              try {
                // Convert snake_case to camelCase before passing to callback
                const newLog = {
                  id: payload?.new?.id,
                  containerId: payload?.new?.container_id,
                  webhookUrl: payload?.new?.webhook_url,
                  payload: payload?.new?.payload,
                  status: payload?.new?.status,
                  httpStatusCode: payload?.new?.http_status_code,
                  responseBody: payload?.new?.response_body,
                  errorMessage: payload?.new?.error_message,
                  retryCount: payload?.new?.retry_count || 0,
                  lastRetryAt: payload?.new?.last_retry_at,
                  createdAt: payload?.new?.created_at,
                  updatedAt: payload?.new?.updated_at
                };
                onInsert(newLog);
              } catch (err) {
                console.error('Error processing INSERT payload:', err);
              }
            }
          }
        )
        ?.on(
          'postgres_changes',
          { 
            event: 'UPDATE', 
            schema: 'public', 
            table: 'n8n_webhook_logs'
            // Remove filter to avoid schema binding issues
          },
          (payload) => {
            console.log('🔄 Webhook log updated:', payload);
            if (onUpdate && payload?.new) {
              try {
                // Convert snake_case to camelCase before passing to callback
                const updatedLog = {
                  id: payload?.new?.id,
                  containerId: payload?.new?.container_id,
                  webhookUrl: payload?.new?.webhook_url,
                  payload: payload?.new?.payload,
                  status: payload?.new?.status,
                  httpStatusCode: payload?.new?.http_status_code,
                  responseBody: payload?.new?.response_body,
                  errorMessage: payload?.new?.error_message,
                  retryCount: payload?.new?.retry_count || 0,
                  lastRetryAt: payload?.new?.last_retry_at,
                  createdAt: payload?.new?.created_at,
                  updatedAt: payload?.new?.updated_at
                };
                onUpdate(updatedLog);
              } catch (err) {
                console.error('Error processing UPDATE payload:', err);
              }
            }
          }
        );

      // Optionally handle DELETE events
      if (onDelete) {
        channel?.on(
          'postgres_changes',
          { 
            event: 'DELETE', 
            schema: 'public', 
            table: 'n8n_webhook_logs' 
          },
          (payload) => {
            console.log('🗑️ Webhook log deleted:', payload);
            if (payload?.old?.id) {
              onDelete(payload?.old?.id);
            }
          }
        );
      }

      // Subscribe with enhanced error handling
      channel?.subscribe((status, err) => {
        console.log('📡 Subscription status:', status, err);
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to n8n_webhook_logs realtime updates');
          if (onConnectionChange) {
            onConnectionChange(true, null);
          }
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Failed to subscribe to n8n_webhook_logs realtime updates:', err);
          
          // Provide specific error message for schema mismatch
          const errorMessage = err?.message?.toLowerCase()?.includes('mismatch')
            ? 'Schema mismatch detected - realtime subscription may be incompatible with complex data types' : err?.message ||'Channel error occurred';
          
          if (onConnectionChange) {
            onConnectionChange(false, errorMessage);
          }
        } else if (status === 'TIMED_OUT') {
          console.error('⏱️ Subscription timed out');
          if (onConnectionChange) {
            onConnectionChange(false, 'Connection timed out');
          }
        } else if (status === 'CLOSED') {
          console.warn('🔌 Channel closed');
          if (onConnectionChange) {
            onConnectionChange(false, 'Connection closed');
          }
        }
      });

      return channel;
    } catch (error) {
      console.error('💥 Error setting up webhook logs subscription:', error);
      if (onConnectionChange) {
        onConnectionChange(false, error?.message || 'Unknown error');
      }
      throw error;
    }
  },

  /**
   * Unsubscribe from real-time webhook log updates
   * @param {Object} channel - Subscription channel to unsubscribe
   */
  unsubscribeFromWebhookUpdates(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
      console.log('🔌 Unsubscribed from n8n_webhook_logs realtime updates');
    }
  },

  /**
   * Get n8n provider configuration
   * @returns {Promise<Object>} N8n provider configuration
   */
  async getN8nProviderConfig() {
    try {
      const { data, error } = await supabase
        ?.from('whatsapp_webhook_config')
        ?.select('*')
        ?.eq('provider', 'n8n')
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error; // Ignore not found error

      if (!data) {
        return {
          webhookUrl: '',
          apiKey: '',
          enabled: false
        };
      }

      return {
        webhookUrl: data?.webhook_url || '',
        apiKey: data?.api_key || '',
        enabled: data?.is_active || false,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (err) {
      console.error('Error fetching n8n provider config:', err);
      return {
        webhookUrl: '',
        apiKey: '',
        enabled: false
      };
    }
  },

  /**
   * Save n8n provider configuration
   * @param {Object} config - N8n provider configuration
   * @returns {Promise<Object>} Saved configuration
   */
  async saveN8nProviderConfig(config) {
    try {
      const { data, error } = await supabase
        ?.from('whatsapp_webhook_config')
        ?.upsert({
          provider: 'n8n',
          webhook_url: config?.webhookUrl,
          api_key: config?.apiKey || null,
          is_active: config?.enabled || false,
          updated_at: new Date()?.toISOString()
        }, {
          onConflict: 'provider'
        })
        ?.select()
        ?.single();

      if (error) throw error;

      console.log('✅ N8n provider configuration saved:', data);

      return {
        webhookUrl: data?.webhook_url,
        apiKey: data?.api_key,
        enabled: data?.is_active,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (err) {
      console.error('Error saving n8n provider config:', err);
      throw err;
    }
  }
};